<template>
    <form class="mform" @submit.prevent="onSubmit">
        <div class="mform__grid">
            <AdmField v-model="form.name" label="Nombre" required maxlength="120" />
            <AdmField v-model="form.manufacturer" label="Fabricante" maxlength="120" />
            <AdmField v-model="form.costPerKg" label="Costo por kg (COP)" type="number" :step="0.01" :min="0" required />
            <AdmField
                :model-value="formatNumber(costPerG, 4)"
                label="Costo por gramo (COP)"
                readonly
                hint="Calculado a partir del costo por kg."
            />
            <AdmField v-model="form.densityGcm3" label="Densidad (g/cm³)" type="number" :step="0.001" :min="0.001" required />
            <AdmField v-model="form.profitPercent" label="% Ganancia estándar" type="number" :step="0.001" :min="0" :max="9999.999" required />
            <AdmField
                v-model="form.printDensityPercent"
                label="% Densidad por defecto (fallback)"
                type="number" :step="0.1" :min="0.1" :max="100" required
                hint="Se usa si la categoría elegida no tiene densidad propia. 100 % = macizo."
            />
            <AdmField v-model="form.maxVolumeCm3" label="Volumen máximo umbral (cm³)" type="number" :step="0.001" :min="0" />
            <AdmField
                v-model="form.surchargePercent"
                label="% Ganancia para volumen grande"
                type="number" :step="0.001" :min="0" :max="9999.999"
                hint="Reemplaza a la ganancia estándar cuando vol ≥ umbral."
            />
            <label v-if="editing" class="mform__check">
                <input v-model="form.isActive" type="checkbox" /> Activo
            </label>
        </div>

        <fieldset class="mform__fieldset">
            <legend>Densidad de impresión (infill) por categoría — "Tipo de impresión"</legend>
            <p class="adm-muted mform__legend-hint">
                Configure el % de relleno para cada categoría. Si una categoría queda vacía, el costeo
                usa la <strong>densidad por defecto</strong> de arriba. La categoría se autoselecciona en
                la cotización pública según el volumen de la pieza.
            </p>

            <AdmEmptyState v-if="!categories.length" message="No hay categorías aún. Crea la primera abajo." />

            <div v-else class="mform__densities">
                <AdmField
                    v-for="c in categories"
                    :key="c.id"
                    v-model="form.densities[c.id]"
                    :label="c.label"
                    :hint="formatRange(c)"
                    type="number" :step="0.1" :min="0.1" :max="100"
                    placeholder="(fallback)"
                />
            </div>

            <div class="mform__new-category">
                <AdmButton v-if="!showNewCategory" variant="secondary" size="sm" :icon="Plus" @click="showNewCategory = true">
                    Nueva categoría
                </AdmButton>
                <div v-else class="mform__new-category-form">
                    <AdmField v-model="newCategory.label" label="Nombre" maxlength="80" placeholder="Ej. Pieza funcional" required />
                    <AdmField v-model="newCategory.minVolumeCm3" label="Vol. mín (cm³)" type="number" :step="0.001" :min="0" placeholder="(opcional)" />
                    <AdmField v-model="newCategory.maxVolumeCm3" label="Vol. máx (cm³)" type="number" :step="0.001" :min="0" placeholder="(opcional)" />
                    <div class="mform__new-category-actions">
                        <AdmButton size="sm" @click="onCreateCategory">Crear</AdmButton>
                        <AdmButton variant="ghost" size="sm" @click="cancelNewCategory">Cancelar</AdmButton>
                    </div>
                    <p class="adm-muted mform__span-all">
                        Deja los volúmenes vacíos para una categoría de selección manual (no entra en la
                        autoselección por volumen).
                    </p>
                </div>
            </div>
        </fieldset>

        <fieldset class="mform__fieldset">
            <legend>Costeo avanzado (energía, máquina y ganancia) — opcional</legend>
            <p class="adm-muted mform__legend-hint">
                Complete <strong>todo este bloque</strong> para activar la estimación precisa de calculo3D
                (energía + amortización + margen de error + multiplicador). Si lo deja vacío, el material
                usa el costeo por peso simple.
                <AdmBadge
                    :tone="usesDetailedModel ? 'ok' : 'neutral'"
                    :label="`Modelo activo: ${usesDetailedModel ? 'AVANZADO' : 'por peso (simple)'}`"
                />
            </p>

            <div class="mform__grid">
                <AdmField
                    v-model="form.kwhPriceCop"
                    label="Tarifa de energía (COP/kWh)"
                    tooltip="Costo del kilovatio-hora de tu proveedor eléctrico. En Colombia suele estar entre 600 y 900 COP."
                    type="number" :step="0.01" :min="0" placeholder="Ej. 700"
                />
                <AdmField
                    v-model="form.printerPowerW"
                    label="Consumo de la impresora (W)"
                    tooltip="Potencia eléctrica promedio durante la impresión. Una FDM de escritorio consume entre 80 y 150 W."
                    type="number" :step="0.01" :min="0" placeholder="Ej. 120"
                />
                <AdmField
                    v-model="form.machineLifeHours"
                    label="Vida útil de la máquina (horas)"
                    tooltip="Horas de operación antes de reponer la máquina. Valor típico: 4000 a 6000 horas."
                    type="number" :step="0.01" :min="0" placeholder="Ej. 4320"
                />
                <AdmField
                    v-model="form.sparePartsCostCop"
                    label="Costo de repuestos / amortización (COP)"
                    tooltip="Presupuesto de mantenimiento o valor de la máquina a amortizar. Ejemplo: 600 000 COP."
                    type="number" :step="0.01" :min="0" placeholder="Ej. 600000"
                />
                <AdmField
                    v-model="form.timePerGramMin"
                    label="Constante de tiempo Kₜ (min/g)"
                    tooltip="Minutos de impresión por gramo (estimación sin slicer). PLA a 0,2 mm de capa: ~10 a 14 min/g."
                    type="number" :step="0.001" :min="0" placeholder="Ej. 12"
                />
                <AdmField
                    v-model="form.profitMultiplier"
                    label="Multiplicador de ganancia"
                    tooltip="Factor sobre el costo de fabricación. 3 = mayorista, 4 = minorista, 5 = llaveros / micro-piezas."
                    type="number" :step="0.001" :min="0" placeholder="Ej. 4"
                    list="profit-multipliers"
                />
                <datalist id="profit-multipliers">
                    <option value="3">Mayorista</option>
                    <option value="4">Minorista</option>
                    <option value="5">Llaveros / micro-piezas</option>
                </datalist>
                <AdmField
                    v-model="form.insumosCostCop"
                    label="Costo de insumos por pieza (COP)"
                    tooltip="Costo de los insumos extra aplicados a cada pieza (tornillería, imanes, insertos, empaques). Se suma al precio como costo × (1 + recargo %). Déjalo en 0 si la pieza no lleva insumos."
                    type="number" :step="0.01" :min="0" placeholder="Ej. 0"
                />
                <AdmField
                    v-model="form.insumosMarkupPercent"
                    label="Recargo de insumos (%)"
                    tooltip="Recargo sobre el costo de insumos para asegurar rentabilidad de la cadena de suministro. El documento sugiere 30 %."
                    type="number" :step="0.001" :min="0" placeholder="Ej. 30"
                />
                <AdmField
                    v-model="form.errorMarginPercent"
                    label="Margen de error (%)"
                    tooltip="Sobrecosto por impresiones fallidas, soportes y purga. Valor típico: 5 a 10 %."
                    type="number" :step="0.001" :min="0" placeholder="Ej. 5"
                />
                <AdmField
                    v-model="form.mlCommissionPercent"
                    label="% Comisión MercadoLibre"
                    tooltip="Opcional. Comisión de la plataforma por categoría; con ella se calcula el Precio MercadoLibre. Típico: 12 a 16 %."
                    type="number" :step="0.001" :min="0" :max="99.999" placeholder="Ej. 15"
                />
                <AdmField
                    v-model="form.mlFixedFeeCop"
                    label="Cargo fijo por publicación (COP)"
                    tooltip="Opcional. Cargo fijo que MercadoLibre cobra por ventas de bajo monto. Ejemplo: 1 000 COP."
                    type="number" :step="0.01" :min="0" placeholder="Ej. 1000"
                />
            </div>
        </fieldset>

        <AdmField v-model="form.description" label="Descripción" type="textarea" :rows="2" maxlength="500" />

        <fieldset class="mform__fieldset">
            <legend>Previsualización del cálculo</legend>

            <div class="mform__preview-controls">
                <AdmField v-model="previewVolumeCm3" label="Volumen de prueba (cm³)" type="number" :step="0.001" :min="0" placeholder="Ej. 50" />
                <AdmField v-if="categories.length" v-model="previewCategoryId" label="Tipo de impresión" type="select">
                    <option :value="null">(densidad por defecto)</option>
                    <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.label }}</option>
                </AdmField>
            </div>

            <p v-if="previewVolumeCm3 > 0" class="adm-muted">
                Infill aplicado en esta previsualización: <strong>{{ formatNumber(activeInfillPercent, 3) }} %</strong>
                <span v-if="previewCategoryId && form.densities[previewCategoryId] == null"> (fallback)</span>
            </p>

            <!-- Desglose del MODELO AVANZADO (calculo3D §3.1–§3.8) -->
            <ul class="mform__results" v-if="usesDetailedModel">
                <li v-if="previewVolumeCm3 > 0">
                    Infill aplicado: <strong>{{ form.printDensityPercent || 100 }} %</strong>
                </li>
                <li v-if="previewVolumeCm3 > 0">
                    Peso estimado (con infill):
                    <strong>{{ formatNumber(pricing.weightG, 3) }} g</strong>
                </li>
                <li v-if="previewVolumeCm3 > 0">
                    Tiempo estimado (Kₜ):
                    <strong>{{ formatNumber(pricing.estTimeMin, 1) }} min</strong>
                    ({{ formatNumber(pricing.estTimeHours, 2) }} h)
                </li>
                <li v-if="previewVolumeCm3 > 0">§3.1 Material: <strong>{{ formatCurrency(pricing.precioMaterial) }}</strong></li>
                <li v-if="previewVolumeCm3 > 0">§3.2 Luz: <strong>{{ formatCurrency(pricing.precioLuz) }}</strong></li>
                <li v-if="previewVolumeCm3 > 0">§3.3 Desgaste máquina: <strong>{{ formatCurrency(pricing.desgaste) }}</strong></li>
                <li v-if="previewVolumeCm3 > 0">§3.4 Margen de error: <strong>{{ formatCurrency(pricing.margenError) }}</strong></li>
                <li v-if="previewVolumeCm3 > 0">§3.5 Costo total (sin insumos): <strong>{{ formatCurrency(pricing.costoTotal) }}</strong></li>
                <li v-if="previewVolumeCm3 > 0 && pricing.insumos > 0">§3.6 Insumos (+{{ form.insumosMarkupPercent ?? 30 }} %): <strong>{{ formatCurrency(pricing.insumos) }}</strong></li>
                <li class="mform__result-final" v-if="previewVolumeCm3 > 0">
                    §3.7 Total a Cobrar (× {{ form.profitMultiplier }}):
                    <strong>{{ formatCurrency(pricing.finalPrice) }}</strong>
                </li>
                <li v-if="previewVolumeCm3 > 0 && pricing.mlPrice != null">
                    §3.8 Precio MercadoLibre:
                    <strong>{{ formatCurrency(pricing.mlPrice) }}</strong>
                </li>
            </ul>

            <!-- Desglose del MODELO LEGACY (costeo por peso 0006) -->
            <ul class="mform__results" v-else>
                <li>
                    Costo por gramo:
                    <strong>{{ formatCurrency(pricing.costPerG) }}</strong>
                </li>
                <li v-if="previewVolumeCm3 > 0">
                    Infill aplicado: <strong>{{ form.printDensityPercent || 100 }} %</strong>
                </li>
                <li v-if="previewVolumeCm3 > 0">
                    Peso estimado (con infill):
                    <strong>{{ formatNumber(pricing.weightG, 3) }} g</strong>
                </li>
                <li v-if="previewVolumeCm3 > 0">
                    Costo base (peso × costo/g):
                    <strong>{{ formatCurrency(pricing.costBase) }}</strong>
                </li>
                <li v-if="hasSurchargeConfig">
                    Umbral: <strong>{{ form.maxVolumeCm3 }} cm³</strong>
                    — Ganancia volumen grande: <strong>{{ form.surchargePercent }} %</strong>
                </li>
                <li v-if="previewVolumeCm3 > 0" :class="{ 'mform__result-surcharge': pricing.overThreshold }">
                    Ganancia aplicada:
                    <strong>{{ formatNumber(pricing.profitAppliedPercent, 3) }} %</strong>
                    <span v-if="pricing.overThreshold"> (volumen ≥ umbral)</span>
                </li>
                <li class="mform__result-final" v-if="previewVolumeCm3 > 0">
                    Precio FINAL estimado:
                    <strong>{{ formatCurrency(pricing.finalPrice) }}</strong>
                </li>
            </ul>
        </fieldset>

        <div class="mform__actions">
            <AdmButton variant="ghost" @click="$emit('cancel')">Cancelar</AdmButton>
            <AdmButton type="submit">{{ editing ? 'Guardar cambios' : 'Crear material' }}</AdmButton>
        </div>
    </form>
</template>

<script>
import { Plus } from 'lucide-vue-next';
import AdmField from '@/components/Admin/ui/AdmField.vue';
import AdmButton from '@/components/Admin/ui/AdmButton.vue';
import AdmBadge from '@/components/Admin/ui/AdmBadge.vue';
import AdmEmptyState from '@/components/Admin/ui/AdmEmptyState.vue';
import { formatNumberRAE, formatCurrencyRAE } from '@/utils/numberFormat';

// ============================================================================
// ESPEJO de apps/server/src/services/print3dPricingService.js
// Mantener AMBOS archivos sincronizados. El servidor es la fuente de verdad.
// ============================================================================
const num = (v) => (v != null && v !== '' && isFinite(Number(v)) ? Number(v) : 0);

// Resuelve el % de infill: override de categoría (opts.densityPercent) →
// fallback material.printDensityPercent → 100. Espejo de print3dPricingService.
function resolveInfillPercent(material, opts = {}) {
    const candidate = opts.densityPercent != null && opts.densityPercent !== ''
        ? Number(opts.densityPercent)
        : (material.printDensityPercent != null && material.printDensityPercent !== ''
            ? Number(material.printDensityPercent)
            : 100);
    return isFinite(candidate) ? candidate : 100;
}

// Costeo por peso legacy (migración 0006). Fallback cuando el material no tiene
// configurado el modelo holístico.
function computePriceFromVolume(material, volumeMm3, opts = {}) {
    const costPerKg = num(material.costPerKg);
    const density = num(material.densityGcm3);
    const infill = resolveInfillPercent(material, opts) / 100;
    const profitStd = num(material.profitPercent) / 100;
    const profitLarge = material.surchargePercent != null && material.surchargePercent !== ''
        ? Number(material.surchargePercent) / 100
        : null;

    const threshold = material.maxVolumeMm3 != null && material.maxVolumeMm3 !== ''
        ? Number(material.maxVolumeMm3) : null;
    const overThreshold = threshold != null && volumeMm3 != null && Number(volumeMm3) >= threshold;
    const profitApplied = (overThreshold && profitLarge != null) ? profitLarge : profitStd;

    const costPerG = costPerKg / 1000;
    const weightG = volumeMm3 != null && density > 0
        ? (Number(volumeMm3) * density * infill) / 1000
        : 0;
    const costBase = weightG * costPerG;
    const finalPrice = costBase * (1 + profitApplied);

    return {
        model: 'legacy',
        costPerG,
        weightG,
        costBase,
        finalPrice,
        estTimeMin: null,
        mlPrice: null,
        profitAppliedPercent: profitApplied * 100,
        overThreshold,
    };
}

// ¿Tiene el material los parámetros mínimos del modelo holístico (calculo3D)?
function hasDetailedPricingConfig(m) {
    const present = (v) => v != null && v !== '' && isFinite(Number(v));
    return (
        present(m.profitMultiplier) &&
        present(m.kwhPriceCop) &&
        present(m.printerPowerW) &&
        present(m.machineLifeHours) && Number(m.machineLifeHours) > 0 &&
        present(m.sparePartsCostCop) &&
        present(m.timePerGramMin)
    );
}

// Costeo holístico fiel a calculo3D.md §3.1–§3.8 y §5.2.
function computeDetailedPrice(material, volumeMm3, opts = {}) {
    const costPerKg = num(material.costPerKg);
    const density = num(material.densityGcm3);
    const infill = resolveInfillPercent(material, opts) / 100;

    const weightG = volumeMm3 != null && density > 0
        ? (Number(volumeMm3) * density * infill) / 1000
        : 0;

    // Tiempo total (h): tiempo real de slicer si llega, si no la heurística K_t (§5.2).
    const hasManualTime = opts.printHours != null || opts.printMinutes != null;
    const Th = hasManualTime
        ? num(opts.printHours) + num(opts.printMinutes) / 60
        : (weightG * num(material.timePerGramMin)) / 60;

    const precioMaterial = (costPerKg / 1000) * weightG;                          // §3.1
    const precioLuz = (num(material.printerPowerW) / 1000) * Th * num(material.kwhPriceCop); // §3.2
    const machineLife = num(material.machineLifeHours);
    const desgaste = machineLife > 0 ? (num(material.sparePartsCostCop) / machineLife) * Th : 0; // §3.3
    const margenError = (precioMaterial + precioLuz + desgaste) * (num(material.errorMarginPercent) / 100); // §3.4
    const costoTotal = precioMaterial + precioLuz + desgaste + margenError;       // §3.5

    const insumosBase = opts.insumosExtraCop != null && opts.insumosExtraCop !== ''
        ? num(opts.insumosExtraCop) : num(material.insumosCostCop);
    const insumosMarkup = material.insumosMarkupPercent != null && material.insumosMarkupPercent !== ''
        ? num(material.insumosMarkupPercent) / 100 : 0.30;
    const insumos = insumosBase * (1 + insumosMarkup);                          // §3.6
    const totalACobrar = (costoTotal * num(material.profitMultiplier)) + insumos; // §3.7

    const mlCommission = material.mlCommissionPercent != null && material.mlCommissionPercent !== ''
        ? num(material.mlCommissionPercent) / 100 : null;
    const mlPrice = (mlCommission != null && mlCommission < 1)
        ? (totalACobrar + num(material.mlFixedFeeCop)) / (1 - mlCommission)
        : null;                                                                  // §3.8

    return {
        model: 'detailed',
        weightG,
        estTimeHours: Th,
        estTimeMin: Th * 60,
        precioMaterial,
        precioLuz,
        desgaste,
        margenError,
        costoTotal,
        insumos,
        finalPrice: totalACobrar,
        mlPrice,
    };
}

// Fachada: holístico si está configurado, si no fallback legacy.
function computeQuotePrice(material, volumeMm3, opts = {}) {
    return hasDetailedPricingConfig(material)
        ? computeDetailedPrice(material, volumeMm3, opts)
        : computePriceFromVolume(material, volumeMm3, opts);
}

export default {
    name: 'MaterialForm',
    components: { AdmField, AdmButton, AdmBadge, AdmEmptyState },
    props: {
        value: { type: Object, default: () => ({}) },
        editing: { type: Boolean, default: false },
        categories: { type: Array, default: () => [] },
    },
    emits: ['submit', 'cancel', 'create-category'],
    data() {
        return {
            Plus,
            form: this.buildInitialForm(this.value),
            previewVolumeCm3: 0,
            previewCategoryId: null,
            showNewCategory: false,
            newCategory: { label: '', minVolumeCm3: '', maxVolumeCm3: '' },
        };
    },
    watch: {
        value: {
            deep: true,
            handler(v) { this.form = this.buildInitialForm(v); },
        },
    },
    computed: {
        pricing() {
            // Convertir UI (cm³) → backend (mm³) antes de aplicar la fórmula compartida.
            const previewMm3 = Number(this.previewVolumeCm3) > 0
                ? Number(this.previewVolumeCm3) * 1000
                : 0;
            const materialForFormula = {
                ...this.form,
                maxVolumeMm3: this.form.maxVolumeCm3 !== '' && this.form.maxVolumeCm3 != null
                    ? Number(this.form.maxVolumeCm3) * 1000
                    : null,
            };
            // El costo de insumos sale del material (form.insumosCostCop); no se
            // sobreescribe aquí para que la preview refleje exactamente lo guardado.
            // densityPercent: densidad de la categoría elegida en la preview (si la hay).
            return computeQuotePrice(materialForFormula, previewMm3, {
                densityPercent: this.previewDensityOverride,
            });
        },
        // Densidad de la categoría elegida en la preview; null = usar fallback.
        previewDensityOverride() {
            if (!this.previewCategoryId) return null;
            const v = this.form.densities[this.previewCategoryId];
            return v != null && v !== '' ? Number(v) : null;
        },
        // % de infill efectivo mostrado en la preview (categoría o fallback).
        activeInfillPercent() {
            return resolveInfillPercent(this.form, { densityPercent: this.previewDensityOverride });
        },
        usesDetailedModel() {
            return hasDetailedPricingConfig(this.form);
        },
        costPerG() {
            const v = Number(this.form.costPerKg);
            return isFinite(v) ? v / 1000 : 0;
        },
        hasSurchargeConfig() {
            return this.form.maxVolumeCm3 != null && this.form.maxVolumeCm3 !== ''
                && this.form.surchargePercent != null && this.form.surchargePercent !== '';
        },
    },
    methods: {
        buildInitialForm(v) {
            // El backend sigue trabajando en mm³; aquí mostramos el umbral en cm³ por UX.
            const maxVolumeCm3 = v.maxVolumeMm3 != null && v.maxVolumeMm3 !== ''
                ? Number(v.maxVolumeMm3) / 1000
                : '';
            // densitiesByCategory (array) → diccionario { categoryId: percent } para los inputs.
            const densities = {};
            if (Array.isArray(v.densitiesByCategory)) {
                for (const d of v.densitiesByCategory) {
                    if (d && d.categoryId != null) densities[d.categoryId] = Number(d.printDensityPercent);
                }
            }
            return {
                id: v.id ?? null,
                densities,
                name: v.name ?? '',
                manufacturer: v.manufacturer ?? '',
                costPerKg: v.costPerKg ?? '',
                profitPercent: v.profitPercent ?? '',
                maxVolumeCm3,
                surchargePercent: v.surchargePercent ?? '',
                printDensityPercent: v.printDensityPercent != null && v.printDensityPercent !== ''
                    ? Number(v.printDensityPercent)
                    : 100,
                densityGcm3: v.densityGcm3 ?? '',
                // Costeo holístico (calculo3D). Vacío = no configurado → fallback legacy.
                kwhPriceCop: v.kwhPriceCop ?? '',
                printerPowerW: v.printerPowerW ?? '',
                machineLifeHours: v.machineLifeHours ?? '',
                sparePartsCostCop: v.sparePartsCostCop ?? '',
                errorMarginPercent: v.errorMarginPercent != null && v.errorMarginPercent !== ''
                    ? Number(v.errorMarginPercent)
                    : 5,
                timePerGramMin: v.timePerGramMin ?? '',
                insumosCostCop: v.insumosCostCop ?? '',
                insumosMarkupPercent: v.insumosMarkupPercent != null && v.insumosMarkupPercent !== ''
                    ? Number(v.insumosMarkupPercent)
                    : 30,
                profitMultiplier: v.profitMultiplier ?? '',
                mlCommissionPercent: v.mlCommissionPercent ?? '',
                mlFixedFeeCop: v.mlFixedFeeCop ?? '',
                description: v.description ?? '',
                isActive: v.isActive !== undefined ? v.isActive : true,
            };
        },
        formatNumber(value, digits = 2) {
            return formatNumberRAE(value, digits);
        },
        formatCurrency(value) {
            return formatCurrencyRAE(value);
        },
        validate() {
            if (!this.form.name?.trim()) { alert('Nombre es obligatorio.'); return false; }
            if (this.form.costPerKg === '' || this.form.costPerKg == null) {
                alert('Costo por kg es obligatorio.'); return false;
            }
            if (Number(this.form.costPerKg) < 0) {
                alert('Costo por kg no puede ser negativo.'); return false;
            }
            if (this.form.densityGcm3 === '' || this.form.densityGcm3 == null) {
                alert('Densidad es obligatoria (parte de la fórmula de costeo).'); return false;
            }
            if (Number(this.form.densityGcm3) <= 0) {
                alert('Densidad debe ser mayor que 0.'); return false;
            }
            if (this.form.profitPercent === '' || this.form.profitPercent == null) {
                alert('% Ganancia estándar es obligatorio.'); return false;
            }
            if (this.form.printDensityPercent === '' || this.form.printDensityPercent == null) {
                alert('% Densidad de impresión (infill) es obligatorio.'); return false;
            }
            const infill = Number(this.form.printDensityPercent);
            if (!isFinite(infill) || infill <= 0 || infill > 100) {
                alert('% Densidad de impresión debe estar entre 0 (exclusivo) y 100 (inclusivo).');
                return false;
            }
            const hasMax = this.form.maxVolumeCm3 !== '' && this.form.maxVolumeCm3 != null;
            const hasSur = this.form.surchargePercent !== '' && this.form.surchargePercent != null;
            if (hasMax !== hasSur) {
                alert('Si configura ganancia para volumen grande debe llenar AMBOS: umbral y porcentaje.');
                return false;
            }
            // Coherencia del costeo holístico: o se configura el bloque mínimo completo
            // (para activar el modelo avanzado) o se deja vacío (cae al costeo por peso).
            const filled = (v) => v !== '' && v != null;
            const minDetailed = ['profitMultiplier', 'kwhPriceCop', 'printerPowerW',
                'machineLifeHours', 'sparePartsCostCop', 'timePerGramMin'];
            const someDetailed = minDetailed.some((k) => filled(this.form[k]));
            const allDetailed = minDetailed.every((k) => filled(this.form[k]));
            if (someDetailed && !allDetailed) {
                alert('Para activar el costeo avanzado (calculo3D) debe completar: multiplicador de '
                    + 'ganancia, tarifa kWh, consumo (W), vida útil (h), costo de repuestos y constante '
                    + 'K_t (min/g). Déjelos todos vacíos para usar el costeo por peso simple.');
                return false;
            }
            if (filled(this.form.mlCommissionPercent)) {
                const c = Number(this.form.mlCommissionPercent);
                if (!isFinite(c) || c < 0 || c >= 100) {
                    alert('% Comisión MercadoLibre debe estar entre 0 (inclusivo) y 100 (exclusivo).');
                    return false;
                }
            }
            return true;
        },
        onSubmit() {
            if (!this.validate()) return;
            const { maxVolumeCm3, densities, ...rest } = this.form;
            const payload = {
                ...rest,
                maxVolumeMm3: maxVolumeCm3 !== '' && maxVolumeCm3 != null
                    ? Number(maxVolumeCm3) * 1000
                    : null,
                // Diccionario { categoryId: percent } → arreglo que espera el backend.
                // Sólo se envían las categorías con un valor; las vacías = usar fallback.
                densitiesByCategory: Object.entries(densities)
                    .filter(([, pct]) => pct !== '' && pct != null)
                    .map(([categoryId, pct]) => ({
                        categoryId: Number(categoryId),
                        printDensityPercent: Number(pct),
                    })),
            };
            if (payload.surchargePercent === '') payload.surchargePercent = null;
            // Normalizar campos opcionales del costeo holístico: '' → null para
            // que el backend distinga "no configurado" de 0.
            const optionalHolistic = [
                'kwhPriceCop', 'printerPowerW', 'machineLifeHours', 'sparePartsCostCop',
                'errorMarginPercent', 'timePerGramMin', 'insumosCostCop', 'insumosMarkupPercent',
                'profitMultiplier', 'mlCommissionPercent', 'mlFixedFeeCop',
            ];
            for (const key of optionalHolistic) {
                if (payload[key] === '' || payload[key] == null) payload[key] = null;
            }
            this.$emit('submit', payload);
        },
        formatRange(c) {
            const min = c.minVolumeCm3;
            const max = c.maxVolumeCm3;
            if (min == null && max == null) return '(manual)';
            if (min == null) return `< ${max} cm³`;
            if (max == null) return `≥ ${min} cm³`;
            return `${min}–${max} cm³`;
        },
        onCreateCategory() {
            if (!this.newCategory.label || !this.newCategory.label.trim()) {
                alert('El nombre de la categoría es obligatorio.');
                return;
            }
            this.$emit('create-category', {
                label: this.newCategory.label.trim(),
                minVolumeCm3: this.newCategory.minVolumeCm3,
                maxVolumeCm3: this.newCategory.maxVolumeCm3,
            });
            this.cancelNewCategory();
        },
        cancelNewCategory() {
            this.showNewCategory = false;
            this.newCategory = { label: '', minVolumeCm3: '', maxVolumeCm3: '' };
        },
    },
};
</script>

<style scoped>
/* El marco de la ficha (fondo, borde, título) lo pone AdmCard desde la vista.
   Aquí sólo queda la rejilla de campos y el desglose del cálculo. */
.mform {
    display: flex;
    flex-direction: column;
    gap: var(--adm-gap-lg);
}

.mform__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
    gap: var(--adm-gap);
    align-items: start;
}

.mform__check {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: var(--adm-font-sm);
    color: var(--color-text-strong);
}

.mform__fieldset {
    margin: 0;
    padding: var(--adm-gap);
    border: 1px solid var(--adm-border);
    border-radius: var(--adm-radius-sm);
}

.mform__fieldset legend {
    padding: 0 0.4rem;
    font-family: var(--font-subtitle);
    font-size: var(--adm-font-sm);
    font-weight: 700;
    color: var(--color-title);
}

.mform__legend-hint {
    margin: 0 0 var(--adm-gap) 0;
    line-height: 1.45;
}

.mform__densities {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
    gap: var(--adm-gap);
}

.mform__new-category { margin-top: var(--adm-gap); }

.mform__new-category-form {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
    gap: var(--adm-gap);
    align-items: end;
    padding: var(--adm-gap);
    background-color: var(--adm-surface-alt);
    border-radius: var(--adm-radius-sm);
}

.mform__new-category-actions {
    display: flex;
    gap: var(--adm-gap-sm);
}

.mform__span-all {
    grid-column: 1 / -1;
    margin: 0;
}

.mform__preview-controls {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
    gap: var(--adm-gap);
    margin-bottom: var(--adm-gap);
}

.mform__results {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    font-size: var(--adm-font-sm);
}

.mform__result-final {
    margin-top: 0.3rem;
    padding-top: 0.4rem;
    border-top: 1px solid var(--adm-border);
    font-size: var(--adm-font-base);
    color: var(--color-title);
}

.mform__result-surcharge { color: var(--adm-warn-fg); }

.mform__actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--adm-gap-sm);
}
</style>
